import React from 'react';
import { Col, Flex, Row, Table } from 'antd';
import { PageHeaderActions } from '@/components';
import CommonDrawer from '@/components/CommonDrawer';
import CustomPagination from '@/components/CustomPagination';
import CustomDeleteModal from '../../../Modals/CustomDeleteModal';
import useTableScroll from '@/hooks/useTableScroll.js';
import CreateCredentialDrawer from './CreateCredentialDrawer';
import CredentialEditForm from './CredentialEditForm';
import RotateCredentialForm from './RotateCredentialForm';
import type { CredentialsController, CredentialsProps } from '../types';
import styles from './CredentialsView.module.css';

// Deleting is allowed while automations still use a credential (a hard warning,
// not a block) so it can be revoked at once in a security incident.
const DELETE_MESSAGE =
  'This cannot be undone. Any automation that still uses this credential will fail at its next run.';

interface CredentialsViewProps extends CredentialsProps {
  controller: CredentialsController;
}

const CredentialsView: React.FC<CredentialsViewProps> = ({
  controller,
  title,
}) => {
  const tableScroll = useTableScroll();
  const {
    columns,
    credentialList,
    credentialsPermissions,
    deleteModalVisible,
    drawerMode,
    emptyText,
    handleAddCredential,
    handleCloseDrawer,
    handleConfirmDelete,
    handleCredentialCreated,
    handleEditSave,
    handlePageChange,
    handleRotateSecret,
    handleSearch,
    loading,
    page,
    rangeEnd,
    rangeStart,
    saving,
    selectedCredential,
    setDeleteModalVisible,
    totalCount,
    totalPages,
    t,
  } = controller;

  return (
    <>
      <div className='settings-content-wrapper'>
        <PageHeaderActions
          showToggle={false}
          showSearch
          onSearchChange={handleSearch}
          onSearchClear={() => handleSearch('')}
          title={title}
          totalCount={totalCount}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          addButtonText='New credential'
          showAddButton={credentialsPermissions?.is_create}
          onAddClick={handleAddCredential}
        />

        <Table
          columns={columns}
          dataSource={credentialList}
          loading={loading}
          rowKey='id'
          pagination={false}
          locale={{ emptyText }}
          className='settings-table settings-table-action-cols'
          tableLayout='fixed'
          scroll={tableScroll}
        />

        <Row className={styles.paginationRow}>
          <Col className='flex-to-block' xs={24}>
            {credentialList.length > 0 && (
              <Flex className='pagination-alignment'>
                <CustomPagination
                  page={page}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </Flex>
            )}
          </Col>
        </Row>
      </div>

      <CustomDeleteModal
        visible={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        onConfirm={handleConfirmDelete}
        title={`Delete "${selectedCredential?.name ?? 'credential'}"`}
        message={DELETE_MESSAGE}
        cancelText={t('cancel')}
        confirmText='Delete credential'
        confirmColor='var(--danger)'
      />

      <CreateCredentialDrawer
        open={drawerMode === 'create'}
        onClose={handleCloseDrawer}
        onCreated={handleCredentialCreated}
      />

      {/* Mounted only while open, so each drawer starts from a clean form and
          a typed secret never outlives it in the DOM. */}
      {(drawerMode === 'edit' || drawerMode === 'rotate') &&
        selectedCredential && (
          <CommonDrawer open onClose={handleCloseDrawer}>
            {drawerMode === 'edit' && (
              <CredentialEditForm
                credential={selectedCredential}
                saving={saving}
                onSave={handleEditSave}
                onCancel={handleCloseDrawer}
              />
            )}
            {drawerMode === 'rotate' && (
              <RotateCredentialForm
                credential={selectedCredential}
                saving={saving}
                onSave={handleRotateSecret}
                onCancel={handleCloseDrawer}
              />
            )}
          </CommonDrawer>
        )}
    </>
  );
};

export default CredentialsView;
